import {
  CreateEquipmentCategoryMutation,
  CreateEquipmentCategoryMutationVariables,
  EquipmentCategory,
  EquipmentCategoryCreateInput,
  EquipmentCategoryQuery,
  EquipmentCategoryQueryVariables,
  EquipmentCategoryUpdateInput,
  UpdateEquipmentCategoryMutation,
  UpdateEquipmentCategoryMutationVariables,
} from '__generated__/graphql';
import gql from 'graphql-tag';
import { useAxios, useOrganization } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { isNil, orderBy } from 'lodash';
import { convertSvgFileToPng } from 'utils/fileUtils';

export default function useEquipmentCategories() {
  const client = useGraphQLClient();
  const pageAll = useGraphQLPaging();
  const axios = useAxios();
  const org = useOrganization();

  const getEquipmentCategories = async () => {
    try {
      const equipmentCategories = await pageAll<EquipmentCategoryQuery, EquipmentCategoryQueryVariables>(
        {
          document: EQUIPMENT_CATEGORIES_QUERY,
          variables: { first: 500, filter: { organizationId: { equal: org.id }, deletedOn: { isNull: true } } },
        },
        'equipmentCategories'
      );
      return orderBy(equipmentCategories, (cat) => cat.title?.toLowerCase()) as EquipmentCategory[];
    } catch (error) {
      throw Error(`Error fetching equipment categories: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const createEquipmentCategory = async (input: { createInput: EquipmentCategoryCreateInput; image: File | null }) => {
    try {
      const result = await client.request<CreateEquipmentCategoryMutation, CreateEquipmentCategoryMutationVariables>({
        document: CREATE_EQUIPMENT_CATEGORY_MUTATION,
        variables: { equipmentCategory: input.createInput },
      });

      const newCategory = result.createEquipmentCategory;

      if (!newCategory) {
        throw Error('Failed to create equipment category');
      }

      if (input.image) {
        return await addImageToEquipmentCategory({ id: newCategory.id, image: input.image });
      }

      return newCategory;
    } catch (error) {
      throw Error(`Error creating equipment category: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const addImageToEquipmentCategory = async (input: { id: string; image: File }) => {
    if (!(input.image instanceof File)) {
      throw new Error('The provided image is not a valid File object.');
    }

    if (input.image.type === 'image/svg+xml') {
      try {
        input.image = await convertSvgFileToPng(input.image);
      } catch (error) {
        throw new Error(`Failed to convert SVG to PNG: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

    if (!validImageTypes.includes(input.image.type)) {
      throw new Error('Invalid image format.');
    }

    const params = new FormData();
    params.append('id', input.id);
    params.append('image', input.image);

    try {
      const result = await axios.patch('equipment-category', params);
      return result.data;
    } catch (error) {
      throw new Error(`Failed to upload image: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const removeImageFromEquipmentCategory = async (id: string) => {
    const params = new FormData();
    params.append('id', id);
    params.append('image', '');
    try {
      const result = await axios.patch('equipment-category', params);
      return result.data;
    } catch (error) {
      throw new Error(`Failed to remove image: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const editEquipmentCategory = async (input: { editInput: EquipmentCategoryUpdateInput; image: File | null }) => {
    try {
      const result = await client.request<UpdateEquipmentCategoryMutation, UpdateEquipmentCategoryMutationVariables>({
        document: UPDATE_EQUIPMENT_CATEGORY_MUTATION,
        variables: { equipmentCategory: input.editInput },
      });

      const newCategory = result.updateEquipmentCategory;

      if (!newCategory) {
        throw Error('Failed to update equipment category');
      }

      if (input.image) {
        return await addImageToEquipmentCategory({ id: newCategory.id, image: input.image });
      }

      if (!input.image && !isNil(newCategory.imageUrl)) {
        removeImageFromEquipmentCategory(newCategory.id);
      }

      return newCategory;
    } catch (error) {
      throw Error(`Error updating equipment category: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const deleteEquipmentCategory = async (editInput: EquipmentCategoryUpdateInput) => {
    try {
      const result = await client.request<UpdateEquipmentCategoryMutation, UpdateEquipmentCategoryMutationVariables>({
        document: UPDATE_EQUIPMENT_CATEGORY_MUTATION,
        variables: { equipmentCategory: editInput },
      });

      const newCategory = result.updateEquipmentCategory;

      if (!newCategory) {
        throw Error('Failed to delete equipment category');
      }

      return newCategory;
    } catch (error) {
      throw Error(`Error deleting equipment category: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return { getEquipmentCategories, createEquipmentCategory, editEquipmentCategory, deleteEquipmentCategory };
}

export const CREATE_EQUIPMENT_CATEGORY_MUTATION = gql`
  mutation CreateEquipmentCategory($equipmentCategory: EquipmentCategoryCreateInput) {
    createEquipmentCategory(equipmentCategory: $equipmentCategory) {
      id
      title
      createdOn
      deletedOn
    }
  }
`;

export const UPDATE_EQUIPMENT_CATEGORY_MUTATION = gql`
  mutation UpdateEquipmentCategory($equipmentCategory: EquipmentCategoryUpdateInput) {
    updateEquipmentCategory(equipmentCategory: $equipmentCategory) {
      id
      title
      imageUrl
      updatedOn
      deletedOn
    }
  }
`;

export const EQUIPMENT_CATEGORIES_QUERY = gql`
  query EquipmentCategories(
    $after: String
    $filter: EquipmentCategoryFilter
    $sort: [EquipmentCategorySort!]
    $first: Int
  ) {
    equipmentCategories(after: $after, first: $first, filter: $filter, sort: $sort) {
      id
      cursor
      organizationId
      imageId
      title
      review
      imageUrl
      createdOn
      updatedOn
      deletedOn
      submittedOn
    }
  }
`;
